"use client";

import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { AppleIcon } from "./OAuthIcons";

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (cfg: {
          clientId: string;
          scope?: string;
          redirectURI: string;
          state?: string;
          nonce?: string;
          usePopup?: boolean;
        }) => void;
        signIn: () => Promise<unknown>;
      };
    };
  }
}

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

interface AppleSignInButtonProps {
  intent?: "signup" | "login";
  next?: string | null;
}

/**
 * Renders Apple's official "Sign in with Apple" button via Apple's JS SDK.
 *
 * Without NEXT_PUBLIC_APPLE_CLIENT_ID, falls back to a hand-styled button
 * matching the HIG spec exactly (black bg, white logo, SF Pro, 8px radius).
 * The fallback uses Supabase's redirect-style OAuth (signInWithOAuth) which
 * works without any Apple-side configuration beyond Supabase's provider.
 */
export function AppleSignInButton({ intent = "login", next }: AppleSignInButtonProps) {
  const [busy, setBusy] = useState(false);
  const clientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID;
  const verb = intent === "signup" ? "Sign up" : "Sign in";

  useEffect(() => {
    if (!clientId) return;

    const existing = document.querySelector(`script[src="${APPLE_SCRIPT_SRC}"]`);
    const init = () => {
      if (!window.AppleID) return;
      try {
        window.AppleID.auth.init({
          clientId,
          scope: "name email",
          redirectURI: `${window.location.origin}/auth/callback`,
          usePopup: false,
        });
      } catch (e) {
        console.error("AppleID init error", e);
      }
    };
    if (existing) {
      if (window.AppleID) init();
      else existing.addEventListener("load", init, { once: true });
    } else {
      const s = document.createElement("script");
      s.src = APPLE_SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.addEventListener("load", init, { once: true });
      document.head.appendChild(s);
    }
  }, [clientId]);

  async function handleClick() {
    setBusy(true);
    if (clientId && window.AppleID) {
      try {
        await window.AppleID.auth.signIn();
        return;
      } catch (e) {
        console.error("Apple sign-in error", e);
        setBusy(false);
        return;
      }
    }
    // Fallback path: Supabase OAuth redirect
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        redirectTo: `${window.location.origin}/auth/callback${
          next ? `?next=${encodeURIComponent(next)}` : ""
        }`,
      },
    });
    if (error) console.error(error);
    setBusy(false);
  }

  return (
    <button type="button" className="mucka-apple-btn" onClick={handleClick} disabled={busy}>
      <AppleIcon className="mucka-apple-btn-logo" />
      <span>{busy ? "…" : `${verb} with Apple`}</span>
    </button>
  );
}
